import * as React from 'react';
import propz from 'propz';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Tournament } from 'Src/models/tournament';
import { AppUser } from 'Src/views/App/App';
import { Switch } from 'Src/components/Switch/Switch';
import { PUBLIC_SITE_STATUS_OPTIONS, PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/tournament';
import { AGE_GROUPS } from 'Src/consts/school';
import { getSeason } from 'Src/helpers/accessor/accessor';
import { getSUPublicSiteTournamentPage } from 'Src/helpers/link/link';
import { checkTournamentDomain } from 'Src/helpers/service/adminSU/tournamentsTable';

interface Props {
  user: AppUser;
  tournament: Tournament;
  serverError?: string;
  onDomainChange?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onCancel: () => void;
  onSubmit: (data: any) => void | Promise<void>;
}

type FormValues = {
  isPublishOnTheWebsite: boolean;
  isPromoteTournament: boolean;
  publicSiteStatus: string;
  publicSitePassword: string;
  publicSiteTemplateVersion: 'LEGACY' | 'TEAM_TOURNAMENT_V2' | 'INDIVIDUAL_TOURNAMENT_V2';
  publicSiteVisitorInformation: string;
  allowedForFollowing: boolean;
  domain: string;
  link: string;
  disclaimer: string;
  publicBigscreenSiteStatus: string;
  publicBigscreenSitePassword: string;
  publicBigscreenSiteAutoScrollingDelay: number | string;
};

const SettingSchema = Yup.object().shape({
  domain: Yup.string()
    .notRequired()
    .matches(/^[a-z0-9-]*$/, 'Use lowercase letters, digits and hyphens only'),
  publicBigscreenSiteAutoScrollingDelay: Yup.number()
    .typeError('Please enter a valid number')
    .min(0, 'Scrolling delay cannot be negative')
    .required('Scrolling delay is required')
});

const accessOptions = (
  <>
    <option value={PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED}>
      {PUBLIC_SITE_STATUS_OPTIONS.DISABLED}
    </option>
    <option value={PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PUBLIC_AVAILABLE}>
      {PUBLIC_SITE_STATUS_OPTIONS.PUBLIC_AVAILABLE}
    </option>
    <option value={PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PROTECTED}>
      {PUBLIC_SITE_STATUS_OPTIONS.PROTECTED}
    </option>
  </>
);

const toDomainPart = (value: string) =>
  String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getDomainSuggestion = (tournament: Tournament, activeUnionDomain: string) => {
  const unionDomain = toDomainPart(
    activeUnionDomain || propz.get(tournament, ['school', 'domain'], '') || propz.get(tournament, ['schoolName'], '')
  );
  const sport = toDomainPart(propz.get(tournament, ['sport', 'name'], '') || tournament.name || 'tournament');
  const ages = (tournament.ages || [])
    .filter(age => Number.isFinite(Number(age)))
    .map(age => Number(age))
    .sort((a, b) => a - b);
  const ageLabels = ages.map(age => toDomainPart(propz.get(AGE_GROUPS, ['ENGLISH', age], ''))).filter(Boolean);
  const age = ageLabels.length > 1 ? `${ageLabels[0]}-${ageLabels[ageLabels.length - 1]}` : ageLabels[0];
  const genders = { MALE_ONLY: 'boys', FEMALE_ONLY: 'girls', MIXED: 'mixed' };
  const gender = genders[tournament.gender] || '';
  const season = tournament.startTime ? toDomainPart(getSeason(tournament)) : '';

  return [unionDomain, sport, age, gender, season].filter(Boolean).join('-');
};

const DirtyReporter = ({ dirty, onChange }: { dirty: boolean; onChange?: (dirty: boolean) => void }) => {
  React.useEffect(() => {
    if (onChange) onChange(dirty);
  }, [dirty]);
  return null;
};

export const TournamentPublicSiteSettingsForm: React.FC<Props> = ({
  user,
  tournament,
  serverError,
  onDomainChange,
  onDirtyChange,
  onCancel,
  onSubmit
}) => {
  const activeUnionDomain = String(propz.get(user, ['activeSchool', 'domain'], '') || '');
  const bookingPageUrl = getSUPublicSiteTournamentPage(tournament, user);
  const domainSuggestion = getDomainSuggestion(tournament, activeUnionDomain);
  const [suggestionStatus, setSuggestionStatus] = React.useState<
    'idle' | 'checking' | 'available' | 'unavailable' | 'error'
  >('idle');
  React.useEffect(() => {
    let isCurrent = true;
    if (!domainSuggestion) return undefined;
    setSuggestionStatus('checking');
    checkTournamentDomain(user, domainSuggestion, tournament.id)
      .then(result => isCurrent && setSuggestionStatus(result.isAvailable ? 'available' : 'unavailable'))
      .catch(() => isCurrent && setSuggestionStatus('error'));
    return () => {
      isCurrent = false;
    };
  }, [domainSuggestion, tournament.id, user]);
  const initialValues: FormValues = {
    isPublishOnTheWebsite: Boolean(propz.get(tournament, ['isPublishOnTheWebsite'], false)),
    isPromoteTournament: Boolean(propz.get(tournament, ['isPromoteTournament'], false)),
    publicSiteStatus: propz.get(
      tournament,
      ['publicSite', 'status'],
      PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED
    ),
    publicSitePassword: String(propz.get(tournament, ['publicSite', 'password'], '') || ''),
    publicSiteTemplateVersion: propz.get(tournament, ['publicSite', 'templateVersion'], 'LEGACY'),
    publicSiteVisitorInformation: String(propz.get(tournament, ['publicSite', 'visitorInformation'], '') || ''),
    allowedForFollowing: Boolean(propz.get(tournament, ['allowedForFollowing'], false)),
    domain: String(propz.get(tournament, ['domain'], '') || ''),
    link: String(propz.get(tournament, ['link'], '') || ''),
    disclaimer: String(propz.get(tournament, ['disclaimer'], '') || ''),
    publicBigscreenSiteStatus: propz.get(
      tournament,
      ['publicBigscreenSite', 'status'],
      PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED
    ),
    publicBigscreenSitePassword: String(propz.get(tournament, ['publicBigscreenSite', 'password'], '') || ''),
    publicBigscreenSiteAutoScrollingDelay: propz.get(tournament, ['publicBigscreenSite', 'autoScrollingDelay'], 6)
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={SettingSchema}
      validate={values => {
        const isPublicSiteEnabled =
          values.publicSiteStatus !== PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED;
        const isBigscreenEnabled =
          values.publicBigscreenSiteStatus !== PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED;
        if ((isPublicSiteEnabled || isBigscreenEnabled) && !(values.domain || '').trim()) {
          if (isBigscreenEnabled && !isPublicSiteEnabled) {
            return {
              publicBigscreenSiteStatus: 'Domain is required to enable Bigscreen / Tournament TV'
            };
          }
          return {
            domain: 'Domain is required to enable the public tournament site'
          };
        }
        return {};
      }}
      validateOnBlur
      validateOnChange={false}
      onSubmit={values => {
        const dataCopy: any = {
          isPublishOnTheWebsite: Boolean(values.isPublishOnTheWebsite),
          isPromoteTournament: values.isPublishOnTheWebsite && Boolean(values.isPromoteTournament),
          publicSite: {
            status: values.publicSiteStatus,
            password: values.publicSitePassword,
            templateVersion: values.publicSiteTemplateVersion,
            visitorInformation: (values.publicSiteVisitorInformation || '').trim()
          },
          allowedForFollowing: Boolean(values.allowedForFollowing),
          domain: (values.domain || '').trim(),
          link: (values.link || '').trim(),
          disclaimer: values.disclaimer || '',
          publicBigscreenSite: {
            status: values.publicBigscreenSiteStatus,
            password: values.publicBigscreenSitePassword,
            autoScrollingDelay: Number(values.publicBigscreenSiteAutoScrollingDelay || 0)
          }
        };
        if (
          values.publicSiteStatus !== PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PROTECTED ||
          !values.publicSitePassword
        ) {
          delete dataCopy.publicSite.password;
        }
        if (
          values.publicBigscreenSiteStatus !== PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PROTECTED ||
          !values.publicBigscreenSitePassword
        ) {
          delete dataCopy.publicBigscreenSite.password;
        }
        return onSubmit(dataCopy);
      }}
    >
      {({ values, errors, touched, submitCount, isSubmitting, setFieldValue, dirty }) => {
        const invalid = (name: keyof FormValues) =>
          Boolean((errors as any)[name]) && (Boolean((touched as any)[name]) || submitCount > 0);
        const toggle = (
          name: keyof FormValues,
          label: string,
          hint: string,
          disabled = false,
          afterChange?: (checked: boolean) => void
        ) => (
          <Field name={name} key={name}>
            {({ field }) => (
              <div className={`utmPublishForm__toggleRow ${disabled ? 'is-disabled' : ''}`}>
                <div>
                  <strong>{label}</strong>
                  <span>{hint}</span>
                </div>
                <Switch
                  {...field}
                  name={String(name)}
                  checked={Boolean(values[name])}
                  disabled={disabled}
                  onChange={event => {
                    setFieldValue(name, event.target.checked);
                    if (afterChange) afterChange(event.target.checked);
                  }}
                  customClass="utmPublishForm__switch"
                />
              </div>
            )}
          </Field>
        );
        const field = (
          name: keyof FormValues,
          label: string,
          control: React.ReactNode,
          hint?: string,
          wide = false,
          externalError?: string
        ) => (
          <label className={`utmPublishForm__field ${wide ? 'is-wide' : ''}`}>
            <span>{label}</span>
            {control}
            {hint ? <small>{hint}</small> : null}
            {invalid(name) ? <em>{(errors as any)[name]}</em> : null}
            {!invalid(name) && externalError ? <em role="alert">{externalError}</em> : null}
          </label>
        );
        return (
          <Form className="utmPublishForm">
            <DirtyReporter dirty={dirty} onChange={onDirtyChange} />
            <div className="utmPublishForm__body">
              <div className="utmPublishForm__bookingPage">
                <div>
                  <strong>Details / booking page</strong>
                  <span>This page is always available and does not depend on Public site or Bigscreen access.</span>
                </div>
                <a href={bookingPageUrl} target="_blank" rel="noopener noreferrer">
                  Open booking page
                </a>
              </div>
              {submitCount > 0 && invalid('domain') ? (
                <button
                  type="button"
                  className="utmPublishForm__validationAlert"
                  onClick={() => {
                    const input = document.getElementById('tournament-publishing-domain');
                    if (input) {
                      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      input.focus();
                    }
                  }}
                >
                  <strong>Domain is required</strong>
                  <span>{(errors as any).domain}</span>
                </button>
              ) : null}
              <section>
                <header>
                  <b>1</b>
                  <div>
                    <h3>Visibility</h3>
                    <p>What appears where. Each switch is independent of the others.</p>
                  </div>
                </header>
                <div className="utmPublishForm__toggleList">
                  {toggle(
                    'isPublishOnTheWebsite',
                    'Publish tournament on union website',
                    'Appears in the public tournament list.',
                    false,
                    checked => {
                      if (!checked) setFieldValue('isPromoteTournament', false);
                    }
                  )}
                  {toggle(
                    'isPromoteTournament',
                    'Highlight tournament',
                    'Pinned to the top of the list. Needs publishing first.',
                    !values.isPublishOnTheWebsite
                  )}
                  {toggle(
                    'allowedForFollowing',
                    'Allowed for following in mobile app',
                    'Parents and schools can follow live results.'
                  )}
                </div>
              </section>
              <section>
                <header>
                  <b>2</b>
                  <div>
                    <h3>Public tournament page</h3>
                    <p>A standalone page with fixtures, standings and results.</p>
                  </div>
                </header>
                <div className="utmPublishForm__grid">
                  {field(
                    'publicSiteStatus',
                    'Access',
                    <Field component="select" name="publicSiteStatus">
                      {accessOptions}
                    </Field>
                  )}
                  {field(
                    'publicSitePassword',
                    'Password',
                    <Field
                      name="publicSitePassword"
                      disabled={
                        values.publicSiteStatus !== PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PROTECTED
                      }
                      placeholder={
                        values.publicSiteStatus === PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PROTECTED
                          ? 'Set a password'
                          : 'Only for protected access'
                      }
                    />
                  )}
                  {tournament.type === 'TEAM_TOURNAMENT' && field(
                    'publicSiteTemplateVersion',
                    'Website design',
                    <Field component="select" name="publicSiteTemplateVersion">
                      <option value="LEGACY">Classic tournament website</option>
                      <option value="TEAM_TOURNAMENT_V2">New live team tournament website</option>
                    </Field>
                  )}
                  {tournament.type === 'INDIVIDUAL_TOURNAMENT' && field(
                    'publicSiteTemplateVersion',
                    'Website design',
                    <Field component="select" name="publicSiteTemplateVersion">
                      <option value="LEGACY">Classic individual tournament website</option>
                      <option value="INDIVIDUAL_TOURNAMENT_V2">New live individual tournament website</option>
                    </Field>
                  )}
                  {field(
                    'domain',
                    'Domain',
                    <Field name="domain">
                      {({ field: domainField }) => (
                        <div className="utmPublishForm__domainControl">
                          <input
                            {...domainField}
                            id="tournament-publishing-domain"
                            placeholder={`e.g. ${domainSuggestion}`}
                            onChange={event => {
                              domainField.onChange(event);
                              if (onDomainChange) onDomainChange();
                            }}
                          />
                          <button
                            type="button"
                            disabled={suggestionStatus !== 'available'}
                            onClick={() => {
                              setFieldValue('domain', domainSuggestion);
                              if (onDomainChange) onDomainChange();
                            }}
                          >
                            {suggestionStatus === 'checking' ? 'Checking…' : 'Use suggestion'}
                          </button>
                          <small className={`is-${suggestionStatus}`}>
                            {suggestionStatus === 'available' ? 'Suggested domain is available.' : null}
                            {suggestionStatus === 'unavailable' ? 'Suggested domain is already in use.' : null}
                            {suggestionStatus === 'error' ? 'Could not check the suggested domain.' : null}
                          </small>
                        </div>
                      )}
                    </Field>,
                    'Lowercase letters, digits and hyphens only.',
                    false,
                    serverError
                  )}
                  {field('link', 'External link', <Field name="link" placeholder="Optional link to your own site" />)}
                  {field(
                    'publicSiteVisitorInformation',
                    'Visitor information / Getting around',
                    <Field
                      component="textarea"
                      name="publicSiteVisitorInformation"
                      rows={4}
                      placeholder="Explain where events take place, where teams should report and anything visitors need to know on arrival."
                    />,
                    'Optional. Shown as Getting around on the new public tournament website.',
                    true
                  )}
                  {field(
                    'disclaimer',
                    'Disclaimer',
                    <Field
                      component="textarea"
                      name="disclaimer"
                      rows={3}
                      placeholder="Optional text shown at the foot of the public page."
                    />,
                    undefined,
                    true
                  )}
                </div>
              </section>
              <section>
                <header>
                  <b>3</b>
                  <div>
                    <h3>Bigscreen / Tournament TV</h3>
                    <p>Control access to the venue screen. Slides, timing and appearance are configured in Tournament TV settings.</p>
                  </div>
                </header>
                <div className="utmPublishForm__grid">
                  {field(
                    'publicBigscreenSiteStatus',
                    'Access',
                    <Field id="tournament-bigscreen-access" component="select" name="publicBigscreenSiteStatus">
                      {accessOptions}
                    </Field>
                  )}
                  {field(
                    'publicBigscreenSitePassword',
                    'Password',
                    <Field
                      name="publicBigscreenSitePassword"
                      disabled={
                        values.publicBigscreenSiteStatus !==
                        PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PROTECTED
                      }
                      placeholder={
                        values.publicBigscreenSiteStatus ===
                        PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PROTECTED
                          ? 'Set a password'
                          : 'Only for protected access'
                      }
                    />
                  )}
                </div>
              </section>
            </div>
            <footer>
              <button type="button" className="is-secondary" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                onClick={() => {
                  const requiresDomain =
                    values.publicSiteStatus !== PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED ||
                    values.publicBigscreenSiteStatus !== PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED;
                  if (requiresDomain && !(values.domain || '').trim()) {
                    window.setTimeout(() => {
                      const isBigscreenOnly =
                        values.publicSiteStatus === PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED &&
                        values.publicBigscreenSiteStatus !==
                          PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED;
                      const input = document.getElementById(
                        isBigscreenOnly ? 'tournament-bigscreen-access' : 'tournament-publishing-domain'
                      );
                      if (input) {
                        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        input.focus();
                      }
                    }, 0);
                  }
                }}
              >
                Save settings
              </button>
            </footer>
          </Form>
        );
      }}
    </Formik>
  );
};

TournamentPublicSiteSettingsForm.displayName = 'TournamentPublicSiteSettingsForm';
